import type { StateCreator, StoreApi, UseBoundStore } from 'zustand';
import { create } from 'zustand';
import type { PersistOptions } from 'zustand/middleware';
import { persist } from 'zustand/middleware';

export interface HydratedState {
  hydrated: boolean;
  onRehydrate: () => void;
}

/**
 * Creates a Zustand store with persistence and hydration tracking.
 * Storage adapter is left to the caller via PersistOptions.storage
 * (default localStorage). Capacitor secure storage can be plugged in per app.
 */
export function createHydratedStorage<
  T extends object,
  PersistedState extends Partial<T & HydratedState> = Partial<
    T & HydratedState
  >,
>(
  stateCreator: StateCreator<T>,
  options: PersistOptions<T & HydratedState, PersistedState>,
): UseBoundStore<StoreApi<T & HydratedState>> {
  const initialStateCreator: StateCreator<T & HydratedState> = (
    set,
    get,
    api,
  ) => {
    const originalState = stateCreator(set, get, api);
    const hydratedState: HydratedState = {
      hydrated: false,
      onRehydrate: () => {
        set((state) => ({ ...state, hydrated: true }));
      },
    };
    return { ...originalState, ...hydratedState };
  };

  return create(
    persist(initialStateCreator, {
      ...options,
      migrate: (persistedState) => {
        return persistedState as PersistedState;
      },
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.onRehydrate();
          if (options.onRehydrateStorage) {
            options.onRehydrateStorage(state);
          }
        }
      },
    }),
  );
}
